import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { MyPurchases } from "@/components/MyPurchases";
import { MarketFieldList } from "@/components/MarketLeadEditor";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShoppingCart, Loader2, ShieldCheck, Clock, Globe } from "lucide-react";

const age = (h) => (h < 1 ? "Under 1 hour old" : h < 48 ? `${Math.round(h)} hours old` : `${Math.round(h / 24)} days old`);

const DaysLeft = ({ id, h }) => {
  if (h == null) return null;
  const last = h < 24;
  const label = last ? `Last day · ${Math.max(1, Math.ceil(h))}h left` : `${Math.ceil(h / 24)} days left`;
  return <Badge className={`border-0 ${last ? "animate-pulse bg-red-100 text-red-700" : h < 72 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`} data-testid={`days-left-${id}`}><Clock className="mr-1 h-3 w-3" />{label}</Badge>;
};

const LeadCard = ({ l, price, busy, onBuy }) => (
  <Card className="flex flex-col p-5" data-testid={`market-lead-${l.id}`}>
    <div className="flex items-start justify-between gap-2">
      <div>
        <div className="text-lg font-bold text-slate-900">{l.loan_purpose || "Mortgage lead"}</div>
        <div className="text-sm text-slate-500">{[l.city, l.state, l.zip_code].filter(Boolean).join(", ") || "Location not provided"}</div>
      </div>
      <div className="flex flex-col items-end gap-1.5">
        <DaysLeft id={l.id} h={l.hours_left} />
        {l.lead_exclusivity && <Badge className={`border-0 ${l.lead_exclusivity === "Exclusive" ? "bg-indigo-100 text-indigo-800" : "bg-sky-100 text-sky-800"}`} data-testid={`exclusivity-${l.id}`}>{l.lead_exclusivity} lead</Badge>}
        {l.consent_documented && <Badge className="border-0 bg-emerald-100 text-emerald-800"><ShieldCheck className="mr-1 h-3 w-3" />Consent on file</Badge>}
      </div>
    </div>
    <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
      <div><dt className="text-xs text-slate-400">LTV</dt><dd className="font-medium">{l.estimated_ltv != null && l.estimated_ltv !== "" ? `${l.estimated_ltv}%` : "—"}</dd></div>
    </dl>
    <MarketFieldList src={l} skipHidden testId={`market-fields-${l.id}`} />
    <div className="mt-2 text-xs text-slate-400">Email, address and phone unlock after purchase</div>
    <div className="mt-3 text-xs text-slate-400">{age(l.age_hours)}</div>
    <Button className="mt-4 bg-slate-900 hover:bg-slate-800" disabled={busy} onClick={() => onBuy(l)} data-testid={`buy-lead-${l.id}`}>
      {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ShoppingCart className="mr-1.5 h-4 w-4" />} Buy lead · ${price}
    </Button>
  </Card>
);

export default function Marketplace() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/marketplace/leads").then((r) => setData(r.data)).catch((e) => setErr(apiError(e))); }, []);
  const buy = async (l) => {
    setBusy(l.id);
    try {
      const { data: d } = await api.post(`/marketplace/leads/${l.id}/checkout`, { origin_url: window.location.origin });
      if (d.free) { toast.success("Lead claimed free - it's in your Leads now"); setBusy(""); api.get("/marketplace/leads").then((r) => setData(r.data)).catch(() => {}); return; }
      window.location.href = d.checkout_url;
    }
    catch (e) { toast.error(apiError(e)); setBusy(""); api.get("/marketplace/leads").then((r) => setData(r.data)).catch(() => {}); }
  };
  return (
    <div>
      <PageHeader title="Lead Marketplace" subtitle="Mortgage leads from Elite Leads. Each card shows whether the lead is shared or exclusive. Contact details unlock after purchase." />
      <div className="mb-5 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900" data-testid="market-requested-banner">
        <Globe className="mt-0.5 h-4 w-4 shrink-0" />
        <span><b>These leads specifically requested mortgage information online.</b> Each card shows the web page the request came from, when available.</span>
      </div>
      {err && <Card className="p-6 text-sm text-slate-600" data-testid="market-closed">{err}</Card>}
      {data && data.leads.length === 0 && <Card className="p-6 text-sm text-slate-500" data-testid="market-empty">No leads are available right now. Check back soon.</Card>}
      {data && <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="market-grid">{data.leads.map((l) => <LeadCard key={l.id} l={l} price={l.price ?? data.price} busy={busy === l.id} onBuy={buy} />)}</div>}
      {data && <MyPurchases />}
      <p className="mt-6 text-xs leading-relaxed text-slate-500" data-testid="market-disclaimer">Marketplace leads are sourced from a lead company that sources its leads from online advertising. Calling these leads is TCPA compliant. Prices include a small markup because they include AI dialing and texting. You are not required to purchase leads; the entire lead cost is included in the $50 minimum to receive 100% commission.</p>
    </div>
  );
}
